import {
  MDSButton,
  MDSIcon,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSIconKind,
} from "@/mds";
import type { IMDSButtonProps } from "@/mds";

import styled from "@emotion/styled";
import { motion } from "motion/react";
import { PropsWithChildren } from "react";

export const WizardContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing[7]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.surface.modal};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  overflow: hidden;
  width: 660px;
  min-height: 80px;
  max-height: min(720px, 95dvh);
  max-width: 90vw;
`;

export const WizardContentWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0; /* Important for flex children to shrink */
`;

/**
 * STEP COMPONENTS
 */

export const StepTitle: React.FC<PropsWithChildren> = ({ children }) => (
  <StyledStepTitle variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
    {children}
  </StyledStepTitle>
);
const StyledStepTitle = styled(MDSText)`
  display: flex;
  padding: 0 ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]}
    ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

export const StyledStepTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const StepContent = styled.div<{ overflowY?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  gap: ${({ theme }) => theme.spacing[4]};
  overflow-y: ${({ overflowY }) => overflowY ?? "unset"};
`;

export const OptionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  flex: 1;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
`;

export const CheckableOption = styled.button<{
  isSelected: boolean;
  isOtherOption?: boolean;
  hasOtherText?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes["text-m"]};
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["3"]} ${({ theme }) => theme.spacing["4"]};

  border: ${({ theme, isSelected, isOtherOption, hasOtherText }) =>
    `1px solid ${isSelected && (!isOtherOption || hasOtherText) ? theme.colors.border.emphasis : theme.colors.border.default}`};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  min-height: 60px;
  color: ${({ theme }) => theme.colors.text.primary};
  height: 54px;

  &:hover {
    border-color: ${({ theme, isSelected, isOtherOption, hasOtherText }) =>
      isSelected && (!isOtherOption || hasOtherText)
        ? theme.colors.border.emphasis
        : theme.colors.border.default};
  }
`;

export const OtherInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

export const FeaturedApp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.card.default};
  padding: ${({ theme }) => theme.spacing["6"]} ${({ theme }) => theme.spacing["8"]};
`;

/** FOOTER COMPONENTS **/

export const FooterContainer = styled.div<{ align?: "flex-end" | "space-between" }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: ${({ align }) => align || "flex-end"};
  gap: ${({ theme }) => theme.spacing["4"]};
  padding-top: ${({ theme }) => theme.spacing[2]};
  padding-inline: ${({ theme }) => theme.spacing[3]};
`;

export const FooterButtonGroup = styled.div<{ justifyContent?: "flex-start" | "space-between" }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ justifyContent }) => justifyContent || "flex-end"};
  gap: ${({ theme }) => theme.spacing[2]};
`;

export const FooterButton = (props: IMDSButtonProps) => (
  <MDSButton variant={MDSButtonVariant.Contrast} {...props} />
);

export const FooterSkipButton = (props: IMDSButtonProps & { isDisabled?: boolean }) => (
  <MDSButton variant={MDSButtonVariant.Transparent} {...props}>
    {props.children}
  </MDSButton>
);

export const FooterBackButton = (props: IMDSButtonProps & { isDisabled?: boolean }) => (
  <MDSButton variant={MDSButtonVariant.Default} {...props}>
    <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.LeftArrow} />
    {props.children}
  </MDSButton>
);

export const FooterNextButton = (props: IMDSButtonProps & { isDisabled?: boolean }) => (
  <MDSButton variant={MDSButtonVariant.Contrast} {...props}>
    {props.children}
    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.RightArrow} />
  </MDSButton>
);
